import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';
import { isAdmin } from 'shared';
import {
  COMMUNITY_GROUP_VISIBILITIES,
  COMMUNITY_PRIVATE_ROOM_MODES,
  COMMUNITY_RULE_TYPES_WITH_NUMBER,
  COMMUNITY_RULE_TYPE_RANK,
  CommunityAdminChannel,
  CommunityAdminChannelPayload,
  CommunityAdminGroup,
  CommunityAdminPermission,
  CommunityAdminRole,
  CommunityAdminService,
  CommunityAdminStats,
  CommunityAdminStructure,
  CommunityAdminUser,
  CommunityAssignment,
  CommunityAssignmentScopeType,
  CommunityAutoRule,
  CommunityChannelMode,
  CommunityCommunicationMode,
  CommunityCommunicationResponse,
  CommunityCommunicationRule,
  CommunityContactGrant,
  CommunityGroupPayload,
  CommunityGroupVisibility,
  CommunityPermissionFlags,
  CommunityPermissionTarget,
  CommunityPrivateRoomAccessLogEntry,
  CommunityPrivateRoomParticipant,
  CommunityReasonModalComponent,
  CommunityRolePayload,
  CommunityRoleVisibility,
  CommunityWebhookDelivery,
  CommunityWebhookEndpoint,
  CommunityWebhookPayload,
  communityErrorKey,
  reactionEmoji
} from 'community';

type AdminTab = 'structure' | 'rights' | 'roles' | 'grants' | 'communication' | 'stats' | 'webhooks';

/** Draft of the webhook create/edit form. */
interface WebhookDraft {
  name: string;
  url: string;
  events: string[];
  active: boolean;
  /** `update` only: request a new signing secret. */
  rotate_secret: boolean;
}

interface RuleDraft {
  from_role_id: number | null;
  to_role_id: number | null;
  mode: CommunityCommunicationMode;
  depth: number;
  description: string;
  active: boolean;
}

interface ContactGrantDraft {
  user: CommunityAdminUser | null;
  user_id: string;
  contact: CommunityAdminUser | null;
  contact_id: string;
  reason: string;
  expires_at: string;
  both_ways: boolean;
}

interface GroupDraft {
  name: string;
  description: string;
  icon: string;
  visibility: CommunityGroupVisibility;
  role_keys: string[];
}

interface ChannelDraft {
  group_id: number;
  name: string;
  description: string;
  mode: CommunityChannelMode;
  read_only: boolean;
  requires_approval: boolean;
  /** '' = every site. */
  config_domain_id: string;
  allowed_uploads: string[];
  reaction_set: string[];
  /** Private Room; once the channel is private it cannot be switched back. */
  private: boolean;
}

/** Draft of the "grant a Private Room participant" form. */
interface ParticipantDraft {
  user: CommunityAdminUser | null;
  /** Typed id -- area admins may not search users (403). */
  user_id: string;
  reason: string;
  expires_at: string;
}

/**
 * Error codes of the Private Room endpoints (`{ error: '<code>' }`), mapped to
 * their texts. Checked before the generic `communityErrorKey`, which only knows
 * `{ code }` payloads.
 */
const PRIVATE_ROOM_ERRORS: Record<string, string> = {
  private_irreversible: 'community.admin.private_room.error_irreversible',
  not_private: 'community.admin.private_room.error_not_private',
  mode_not_allowed_private: 'community.admin.private_room.error_mode',
  already_participant: 'community.admin.private_room.error_already_participant'
};

function privateRoomErrorKey(err: unknown): string | null {
  const code = (err as { error?: { error?: unknown } } | null)?.error?.error;
  return typeof code === 'string' ? PRIVATE_ROOM_ERRORS[code] ?? null : null;
}

interface RoleDraft {
  key: string;
  name: string;
  description: string;
  badge_label: string;
  badge_color: string;
  visibility: CommunityRoleVisibility;
  capabilities: string[];
  /** '' = no automatic rule (admins grant it by hand). */
  rule_type: string;
  rule_value: string;
  position: number;
  active: boolean;
}

interface GrantDraft {
  user: CommunityAdminUser | null;
  user_id: string;
  scope: 'global' | 'group' | 'channel';
  scope_id: number | null;
  reason: string;
  expires_at: string;
}

/** CoreUI theme colours offered for a role badge; the field also accepts any CSS colour. */
const BADGE_COLORS = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'];

/**
 * Community Network administration (Phase 1): groups & channels, the rights
 * matrix, roles and role grants — one page, four tabs, one `structure` signal
 * that is reloaded after every mutation so the screen never drifts from the
 * server. Everything an admin does here is a row change (§30).
 *
 * Reachable in two ways: `/admin/community` (behind adminGuard, like every
 * other admin page) and `/community/admin` inside the community routes, which
 * is how an area admin — a member with `manage_group` on a group, not a
 * `user_type` admin — gets in. The backend decides what either sees: area
 * admins receive only their groups and `can_manage_roles: false`; everyone
 * else gets a 403, which this page shows as "not permitted".
 */
@Component({
  selector: 'app-community-admin-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslatePipe, CommunityReasonModalComponent],
  templateUrl: './community-admin-page.component.html',
  styleUrl: './community-admin-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityAdminPageComponent implements OnInit {
  private admin = inject(CommunityAdminService);
  private translate = inject(TranslateService);
  private i18n = inject(I18nService);
  private auth = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  readonly groupVisibilities = COMMUNITY_GROUP_VISIBILITIES;
  readonly badgeColors = BADGE_COLORS;
  readonly emoji = reactionEmoji;

  // --- Page state ------------------------------------------------------------
  structure = signal<CommunityAdminStructure | null>(null);
  loading = signal(true);
  /** 403 from the structure endpoint: neither admin nor area admin. */
  forbidden = signal(false);
  /** Translation key of the last failed request. */
  error = signal<string | null>(null);
  /** Validation messages the backend returned alongside a 422 (`errors` hash). */
  errorDetail = signal<string | null>(null);
  busy = signal(false);
  tab = signal<AdminTab>('structure');

  roles = computed<CommunityAdminRole[]>(() => this.structure()?.roles ?? []);
  activeRoles = computed<CommunityAdminRole[]>(() => this.roles().filter(r => r.active));
  groups = computed<CommunityAdminGroup[]>(() => this.structure()?.groups ?? []);
  sites = computed(() => this.structure()?.sites ?? []);
  canManageRoles = computed(() => this.structure()?.can_manage_roles === true);
  flags = computed<string[]>(() => this.structure()?.permission_flags ?? []);
  channelModes = computed<CommunityChannelMode[]>(() => this.structure()?.channel_modes ?? ['discussion', 'news', 'qa']);
  uploadKinds = computed<string[]>(() => this.structure()?.upload_kinds ?? []);
  reactionKeys = computed<string[]>(() => this.structure()?.reaction_keys ?? []);
  capabilities = computed<string[]>(() => this.structure()?.capabilities ?? []);
  roleVisibilities = computed<CommunityRoleVisibility[]>(() => this.structure()?.visibilities ?? []);
  rankCodes = computed<string[]>(() => this.structure()?.rank_codes ?? []);
  /** Rule types grouped as the backend evaluates them (live / full / passive). */
  ruleGroups = computed<{ group: string; types: string[] }[]>(() => {
    const rt = this.structure()?.rule_types;
    if (!rt) return [];
    return [
      { group: 'live', types: rt.live ?? [] },
      { group: 'full', types: rt.full ?? [] },
      { group: 'passive', types: rt.passive ?? [] }
    ].filter(g => g.types.length > 0);
  });
  /** Every channel of every manageable group, for move targets and grant scopes. */
  allChannels = computed<{ group: CommunityAdminGroup; channel: CommunityAdminChannel }[]>(() =>
    this.groups().flatMap(group => group.channels.map(channel => ({ group, channel })))
  );

  // --- Structure tab ---------------------------------------------------------
  /** 'new', a group id, or null while closed. */
  groupFormTarget = signal<'new' | number | null>(null);
  groupDraft: GroupDraft = this.emptyGroupDraft();
  /** 'new', a channel id, or null; `channelFormGroupId` says where a new channel goes. */
  channelFormTarget = signal<'new' | number | null>(null);
  channelFormGroupId = signal<number | null>(null);
  channelDraft: ChannelDraft = this.emptyChannelDraft(0);
  /** Channel whose deletion was refused (422: has threads) and now needs a move target. */
  deletePendingChannel = signal<CommunityAdminChannel | null>(null);
  deleteMoveTargetId: number | null = null;
  deleteMoveTargets = computed(() => {
    const pending = this.deletePendingChannel();
    return pending ? this.allChannels().filter(x => x.channel.id !== pending.id) : [];
  });

  // --- Private Rooms (structure tab) -------------------------------------------
  /** Whether the channel being edited is ALREADY private: then the switch is locked on. */
  channelDraftWasPrivate = false;
  /** A public channel about to be turned private: waits for the confirmation dialog. */
  privatizeTarget = signal<{ id: number; name: string; payload: CommunityAdminChannelPayload } | null>(null);
  /** `user_type` admin (or super): only they see the platform-admin access log. */
  isPlatformAdmin = signal(false);
  /** Channel whose participant panel is open (one at a time). */
  participantsChannelId = signal<number | null>(null);
  participants = signal<CommunityPrivateRoomParticipant[]>([]);
  participantsTotal = signal(0);
  participantsLoading = signal(false);
  /** Translation key of the last failure inside the panel. */
  participantsError = signal<string | null>(null);
  participantsErrorDetail = signal<string | null>(null);
  participantBusy = signal(false);
  participantDraft: ParticipantDraft = this.emptyParticipantDraft();
  participantUserResults = signal<CommunityAdminUser[]>([]);
  participantUserSearchTerm = '';
  private participantUserSearch$ = new Subject<string>();
  /** Participant whose access is about to be withdrawn (reason dialog open). */
  participantRemoveTarget = signal<{ channelId: number; participant: CommunityPrivateRoomParticipant } | null>(null);
  participantRemoveError = signal<string | null>(null);
  accessLog = signal<CommunityPrivateRoomAccessLogEntry[]>([]);
  accessLogPage = signal(1);
  accessLogTotalPages = signal(1);
  accessLogLoading = signal(false);

  // --- Rights tab --------------------------------------------------------------
  rightsGroupId = signal<number | null>(null);
  rightsGroup = computed<CommunityAdminGroup | null>(() =>
    this.groups().find(g => g.id === this.rightsGroupId()) ?? null
  );
  /** Roles added to the matrix but not saved yet (no row on the server). */
  extraMatrixRoleIds = signal<Set<number>>(new Set());
  /** Roles shown in the matrix: those opening the group plus any with a row. */
  matrixRoles = computed<CommunityAdminRole[]>(() => {
    const group = this.rightsGroup();
    if (!group) return [];
    const keys = new Set(group.role_keys);
    group.default_permissions.forEach(p => keys.add(p.role_key));
    group.channels.forEach(c => c.permissions.forEach(p => keys.add(p.role_key)));
    const extra = this.extraMatrixRoleIds();
    return this.roles().filter(r => keys.has(r.key) || extra.has(r.id));
  });
  /** Roles that could still be added as a row. */
  addableMatrixRoles = computed<CommunityAdminRole[]>(() => {
    const present = new Set(this.matrixRoles().map(r => r.id));
    return this.activeRoles().filter(r => !present.has(r.id));
  });
  addMatrixRoleId: number | null = null;
  /** Unsaved cell edits, keyed by row (`g:<group>:<role>` / `c:<channel>:<role>`). */
  drafts = signal<Record<string, CommunityPermissionFlags>>({});
  expandedChannels = signal<Set<number>>(new Set());

  // --- Roles tab -----------------------------------------------------------------
  roleFormTarget = signal<'new' | number | null>(null);
  roleDraft: RoleDraft = this.emptyRoleDraft();

  // --- Grants tab ------------------------------------------------------------------
  grantsRoleId = signal<number | null>(null);
  grantsRole = computed<CommunityAdminRole | null>(() => this.roles().find(r => r.id === this.grantsRoleId()) ?? null);
  assignments = signal<CommunityAssignment[]>([]);
  assignmentsLoading = signal(false);
  assignmentsPage = signal(1);
  assignmentsTotalPages = signal(1);
  assignmentsTotal = signal(0);
  includeRevoked = signal(false);
  /** Free-text member filter for the grants list — a role with hundreds of holders is not scrollable. */
  assignmentSearch = signal('');
  assignmentSearchTerm = '';
  private assignmentSearch$ = new Subject<string>();
  grantFormOpen = signal(false);
  grantDraft: GrantDraft = this.emptyGrantDraft();
  userResults = signal<CommunityAdminUser[]>([]);
  userSearchTerm = '';
  private userSearch$ = new Subject<string>();

  // --- Communication tab (rules & contact grants, admins / manage_roles only) ----
  communication = signal<CommunityCommunicationResponse | null>(null);
  communicationLoading = signal(false);
  rules = computed<CommunityCommunicationRule[]>(() => this.communication()?.rules ?? []);
  communicationModes = computed<CommunityCommunicationMode[]>(() => this.communication()?.modes ?? ['any', 'same_group', 'sponsor_line']);
  contactGrants = computed<CommunityContactGrant[]>(() => this.communication()?.grants ?? []);

  ruleFormTarget = signal<'new' | number | null>(null);
  ruleDraft: RuleDraft = this.emptyRuleDraft();

  // --- Statistics tab (Phase 5) -----------------------------------------------
  stats = signal<CommunityAdminStats | null>(null);
  statsLoading = signal(false);
  statsDays = signal(30);

  // --- Webhooks tab (Phase 5, `manage_roles` only) -----------------------------
  webhooks = signal<CommunityWebhookEndpoint[]>([]);
  webhookEvents = signal<string[]>([]);
  webhookDeliveries = signal<CommunityWebhookDelivery[]>([]);
  webhooksLoading = signal(false);
  webhooksLoaded = signal(false);
  webhookFormTarget = signal<'new' | number | null>(null);
  webhookDraft: WebhookDraft = this.emptyWebhookDraft();
  /** The signing secret, shown exactly once right after create or a rotation. */
  webhookSecret = signal<string | null>(null);

  contactGrantFormOpen = signal(false);
  contactGrantDraft: ContactGrantDraft = this.emptyContactGrantDraft();
  contactUserResults = signal<CommunityAdminUser[]>([]);
  contactUserSearchTerm = '';
  contactContactResults = signal<CommunityAdminUser[]>([]);
  contactContactSearchTerm = '';
  private contactUserSearch$ = new Subject<string>();
  private contactContactSearch$ = new Subject<string>();

  ngOnInit(): void {
    const userSearch = (q: string) =>
      q.trim().length < 2
        ? of({ data: [] as CommunityAdminUser[] })
        : this.admin.searchUsers(q.trim()).pipe(catchError(() => of({ data: [] as CommunityAdminUser[] })));

    this.userSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), switchMap(userSearch), takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.userResults.set(res.data));
    this.contactUserSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), switchMap(userSearch), takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.contactUserResults.set(res.data));
    this.contactContactSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), switchMap(userSearch), takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.contactContactResults.set(res.data));
    this.participantUserSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), switchMap(userSearch), takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.participantUserResults.set(res.data));
    // Subscribed, not read once: on a hard reload the user may resolve after the page renders.
    this.auth.getUser$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => this.isPlatformAdmin.set(isAdmin(user)));
    this.assignmentSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(term => {
        this.assignmentSearch.set(term);
        this.loadAssignments(1);
      });
    this.load();
  }

  get lang(): string {
    return this.i18n.getLanguage();
  }

  communityLink(): string {
    return `/${this.lang}/community`;
  }

  // --- Loading ----------------------------------------------------------------

  load(): void {
    this.loading.set(true);
    this.admin.getStructure().subscribe({
      next: res => {
        this.structure.set(res);
        this.forbidden.set(false);
        this.loading.set(false);
        if (this.rightsGroupId() == null || !res.groups.some(g => g.id === this.rightsGroupId())) {
          this.rightsGroupId.set(res.groups[0]?.id ?? null);
        }
        if (this.grantsRoleId() == null && res.roles.length) this.grantsRoleId.set(res.roles[0].id);
        if (!res.can_manage_roles && (this.tab() === 'roles' || this.tab() === 'webhooks')) this.tab.set('structure');
      },
      error: err => {
        this.loading.set(false);
        if (err?.status === 403) this.forbidden.set(true);
        else this.error.set(communityErrorKey(err));
      }
    });
  }

  selectTab(tab: AdminTab): void {
    this.tab.set(tab);
    this.clearError();
    if (tab === 'grants') this.loadAssignments(1);
    if (tab === 'communication' && !this.communication()) this.loadCommunication();
    if (tab === 'stats' && !this.stats()) this.loadStats();
    if (tab === 'webhooks' && this.canManageRoles() && !this.webhooksLoaded()) this.loadWebhooks();
  }

  clearError(): void {
    this.error.set(null);
    this.errorDetail.set(null);
  }

  /** Run a mutation, then reload the structure; 422 validation messages are shown verbatim. */
  private run(request: Observable<unknown>, onSuccess?: () => void): void {
    this.clearError();
    this.busy.set(true);
    request.subscribe({
      next: () => {
        this.busy.set(false);
        onSuccess?.();
        this.load();
      },
      error: err => this.fail(err)
    });
  }

  private fail(err: unknown): void {
    this.busy.set(false);
    const privateRoomKey = privateRoomErrorKey(err);
    if (privateRoomKey) {
      this.error.set(privateRoomKey);
      this.errorDetail.set(null);
      return;
    }
    this.error.set(communityErrorKey(err));
    const errors = (err as { error?: { errors?: unknown; error?: unknown } })?.error?.errors;
    if (errors && typeof errors === 'object') {
      const messages = Object.entries(errors as Record<string, unknown>)
        .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : String(msgs)}`);
      this.errorDetail.set(messages.join(' · '));
    } else {
      const message = (err as { error?: { error?: unknown } })?.error?.error;
      this.errorDetail.set(typeof message === 'string' && this.error() === 'community.errors.generic' ? message : null);
    }
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.translate(key, params);
  }

  roleByKey(key: string): CommunityAdminRole | undefined {
    return this.roles().find(r => r.key === key);
  }

  roleName(roleId: number): string {
    return this.roles().find(r => r.id === roleId)?.name ?? `#${roleId}`;
  }

  // ===========================================================================
  // Structure tab: groups
  // ===========================================================================

  // A new group starts hidden: "not everyone sees everything" (customer decision
  // 2026-09-21). `listed` shows it LOCKED to every member without a role — right for
  // a teaser like the Health Club, wrong as a default an admin never touches.
  private emptyGroupDraft(): GroupDraft {
    return { name: '', description: '', icon: '', visibility: 'hidden_unless_member', role_keys: [] };
  }

  openGroupForm(group?: CommunityAdminGroup): void {
    this.clearError();
    this.channelFormTarget.set(null);
    this.groupDraft = group
      ? {
          name: group.name,
          description: group.description ?? '',
          icon: group.icon ?? '',
          visibility: group.visibility,
          role_keys: [...group.role_keys]
        }
      : this.emptyGroupDraft();
    this.groupFormTarget.set(group ? group.id : 'new');
  }

  cancelGroupForm(): void {
    this.groupFormTarget.set(null);
  }

  toggleDraftRole(key: string): void {
    const keys = this.groupDraft.role_keys;
    this.groupDraft.role_keys = keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key];
  }

  submitGroupForm(): void {
    const target = this.groupFormTarget();
    const name = this.groupDraft.name.trim();
    if (target == null || !name) return;
    const payload: CommunityGroupPayload = {
      name,
      description: this.groupDraft.description.trim() || null,
      icon: this.groupDraft.icon.trim() || null,
      visibility: this.groupDraft.visibility
    };
    // Area admins may not change which roles open a group; the backend ignores
    // role_keys for them, so it is simply not sent.
    if (this.canManageRoles()) payload.role_keys = this.groupDraft.role_keys;
    this.run(
      target === 'new' ? this.admin.createGroup(payload) : this.admin.updateGroup(target, payload),
      () => this.groupFormTarget.set(null)
    );
  }

  toggleGroupArchived(group: CommunityAdminGroup): void {
    this.run(this.admin.updateGroup(group.id, { archived: !group.archived }));
  }

  deleteGroup(group: CommunityAdminGroup): void {
    if (!confirm(this.t('community.admin.group.delete_confirm', { name: group.name }))) return;
    this.run(this.admin.deleteGroup(group.id));
  }

  moveGroup(group: CommunityAdminGroup, delta: -1 | 1): void {
    const ids = this.groups().map(g => g.id);
    const index = ids.indexOf(group.id);
    const swap = index + delta;
    if (index < 0 || swap < 0 || swap >= ids.length) return;
    [ids[index], ids[swap]] = [ids[swap], ids[index]];
    this.run(this.admin.reorderGroups(ids));
  }

  // ===========================================================================
  // Structure tab: channels
  // ===========================================================================

  private emptyChannelDraft(groupId: number): ChannelDraft {
    return {
      group_id: groupId,
      name: '',
      description: '',
      mode: 'discussion',
      read_only: false,
      requires_approval: false,
      config_domain_id: '',
      allowed_uploads: ['image', 'video_link'],
      reaction_set: [...this.reactionKeys()],
      private: false
    };
  }

  openChannelForm(group: CommunityAdminGroup, channel?: CommunityAdminChannel): void {
    this.clearError();
    this.groupFormTarget.set(null);
    this.deletePendingChannel.set(null);
    this.channelFormGroupId.set(group.id);
    this.channelDraft = channel
      ? {
          group_id: channel.group_id,
          name: channel.name,
          description: channel.description ?? '',
          mode: channel.mode,
          read_only: channel.read_only,
          requires_approval: channel.requires_approval,
          config_domain_id: channel.config_domain_id ?? '',
          allowed_uploads: [...(channel.allowed_uploads ?? [])],
          reaction_set: [...(channel.reaction_set ?? [])],
          private: channel.private === true
        }
      : this.emptyChannelDraft(group.id);
    this.channelDraftWasPrivate = channel?.private === true;
    this.channelFormTarget.set(channel ? channel.id : 'new');
  }

  /** Modes offered in the channel form: a Private Room is discussion or Q&A only. */
  channelFormModes(): CommunityChannelMode[] {
    const modes = this.channelModes();
    return this.channelDraft.private ? modes.filter(m => COMMUNITY_PRIVATE_ROOM_MODES.includes(m)) : modes;
  }

  onDraftPrivateChange(value: boolean): void {
    if (this.channelDraftWasPrivate) return; // irreversible: the switch is locked on
    this.channelDraft.private = value;
    if (value && !COMMUNITY_PRIVATE_ROOM_MODES.includes(this.channelDraft.mode)) {
      this.channelDraft.mode = 'discussion';
    }
  }

  cancelChannelForm(): void {
    this.channelFormTarget.set(null);
    this.channelFormGroupId.set(null);
  }

  toggleDraftList(list: 'allowed_uploads' | 'reaction_set', value: string): void {
    const current = this.channelDraft[list];
    this.channelDraft[list] = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
  }

  submitChannelForm(): void {
    const target = this.channelFormTarget();
    const name = this.channelDraft.name.trim();
    if (target == null || !name) return;
    const payload: CommunityAdminChannelPayload = {
      name,
      description: this.channelDraft.description.trim() || null,
      mode: this.channelDraft.mode,
      read_only: this.channelDraft.read_only,
      requires_approval: this.channelDraft.requires_approval,
      config_domain_id: this.channelDraft.config_domain_id || null,
      allowed_uploads: this.channelDraft.allowed_uploads,
      reaction_set: this.channelDraft.reaction_set
    };
    if (target === 'new') {
      payload.group_id = this.channelDraft.group_id;
      if (this.channelDraft.private) payload.private = true;
    } else if (this.channelDraft.private && !this.channelDraftWasPrivate) {
      // Public -> private is irreversible and does not make what was public
      // confidential after the fact: the admin confirms that first.
      payload.private = true;
      this.privatizeTarget.set({ id: target, name, payload });
      return;
    }
    this.run(
      target === 'new' ? this.admin.createChannel(payload) : this.admin.updateChannel(target, payload),
      () => this.cancelChannelForm()
    );
  }

  confirmPrivatize(): void {
    const pending = this.privatizeTarget();
    if (!pending) return;
    this.privatizeTarget.set(null);
    this.run(this.admin.updateChannel(pending.id, pending.payload), () => this.cancelChannelForm());
  }

  cancelPrivatize(): void {
    this.privatizeTarget.set(null);
  }

  siteName(configDomainId: string | null): string | null {
    if (!configDomainId) return null;
    return this.sites().find(s => s.id === configDomainId)?.name ?? `#${configDomainId}`;
  }

  // ===========================================================================
  // Structure tab: Private Rooms (participants, read-only mode, access log)
  // ===========================================================================

  toggleParticipantsReadOnly(channel: CommunityAdminChannel): void {
    this.run(this.admin.updateChannel(channel.id, { participants_read_only: !channel.participants_read_only }));
  }

  private emptyParticipantDraft(): ParticipantDraft {
    return { user: null, user_id: '', reason: '', expires_at: '' };
  }

  toggleParticipants(channel: CommunityAdminChannel): void {
    if (this.participantsChannelId() === channel.id) {
      this.participantsChannelId.set(null);
      return;
    }
    this.participantsChannelId.set(channel.id);
    this.participants.set([]);
    this.participantsTotal.set(0);
    this.accessLog.set([]);
    this.resetParticipantForm();
    this.loadParticipants(channel.id);
    if (this.isPlatformAdmin()) this.loadAccessLog(channel.id, 1);
  }

  private resetParticipantForm(): void {
    this.participantDraft = this.emptyParticipantDraft();
    this.participantUserSearchTerm = '';
    this.participantUserResults.set([]);
    this.participantsError.set(null);
    this.participantsErrorDetail.set(null);
  }

  loadParticipants(channelId: number): void {
    this.participantsLoading.set(true);
    this.admin.listParticipants(channelId).subscribe({
      next: res => {
        if (this.participantsChannelId() !== channelId) return;
        this.participants.set(res.participants ?? []);
        this.participantsTotal.set(res.total_count ?? res.participants?.length ?? 0);
        this.participantsLoading.set(false);
      },
      error: err => {
        this.participantsLoading.set(false);
        this.participantFail(err);
      }
    });
  }

  loadAccessLog(channelId: number, page: number): void {
    this.accessLogLoading.set(true);
    this.admin.accessLog(channelId, page, 10).subscribe({
      next: res => {
        if (this.participantsChannelId() !== channelId) return;
        this.accessLog.set(res.data ?? []);
        this.accessLogPage.set(res.page ?? page);
        this.accessLogTotalPages.set(res.total_pages ?? 1);
        this.accessLogLoading.set(false);
      },
      // The log is supplementary: a failure just leaves the section out.
      error: () => {
        this.accessLog.set([]);
        this.accessLogLoading.set(false);
      }
    });
  }

  onParticipantUserSearch(term: string): void {
    this.participantUserSearchTerm = term;
    this.participantDraft.user = null;
    this.participantUserSearch$.next(term);
  }

  pickParticipantUser(user: CommunityAdminUser): void {
    this.participantDraft.user = user;
    this.participantDraft.user_id = String(user.id);
    this.participantUserSearchTerm = this.userLabel(user);
    this.participantUserResults.set([]);
  }

  participantDraftValid(): boolean {
    const d = this.participantDraft;
    const userId = d.user?.id ?? Number(d.user_id);
    return Number.isInteger(userId) && userId > 0 && !!d.reason.trim();
  }

  addParticipant(channelId: number): void {
    if (!this.participantDraftValid()) return;
    const d = this.participantDraft;
    this.participantsError.set(null);
    this.participantsErrorDetail.set(null);
    this.participantBusy.set(true);
    this.admin.addParticipant(channelId, {
      user_id: d.user?.id ?? Number(d.user_id),
      reason: d.reason.trim(),
      expires_at: d.expires_at || null
    }).subscribe({
      next: () => {
        this.participantBusy.set(false);
        this.resetParticipantForm();
        this.loadParticipants(channelId);
      },
      error: err => {
        this.participantBusy.set(false);
        this.participantFail(err);
      }
    });
  }

  askRemoveParticipant(channelId: number, participant: CommunityPrivateRoomParticipant): void {
    this.participantRemoveError.set(null);
    this.participantRemoveTarget.set({ channelId, participant });
  }

  confirmRemoveParticipant(reason: string): void {
    const target = this.participantRemoveTarget();
    if (!target || !reason.trim()) return;
    this.participantBusy.set(true);
    this.participantRemoveError.set(null);
    this.admin.removeParticipant(target.channelId, target.participant.id, reason.trim()).subscribe({
      next: () => {
        this.participantBusy.set(false);
        this.participantRemoveTarget.set(null);
        this.loadParticipants(target.channelId);
      },
      error: err => {
        this.participantBusy.set(false);
        this.participantRemoveError.set(privateRoomErrorKey(err) ?? communityErrorKey(err));
      }
    });
  }

  cancelRemoveParticipant(): void {
    if (this.participantBusy()) return;
    this.participantRemoveTarget.set(null);
  }

  private participantFail(err: unknown): void {
    this.participantsError.set(privateRoomErrorKey(err) ?? communityErrorKey(err));
    const errors = (err as { error?: { errors?: unknown } })?.error?.errors;
    if (errors && typeof errors === 'object') {
      const messages = Object.entries(errors as Record<string, unknown>)
        .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : String(msgs)}`);
      this.participantsErrorDetail.set(messages.join(' · '));
    } else {
      this.participantsErrorDetail.set(null);
    }
  }

  privateRoomUserLabel(user: { full_name?: string | null; username?: string | null; email?: string | null } | null | undefined): string {
    if (!user) return '—';
    return user.full_name || user.username || user.email || '—';
  }

  toggleChannelArchived(channel: CommunityAdminChannel): void {
    this.run(this.admin.updateChannel(channel.id, { archived: !channel.archived }));
  }

  moveChannel(group: CommunityAdminGroup, channel: CommunityAdminChannel, delta: -1 | 1): void {
    const ids = group.channels.map(c => c.id);
    const index = ids.indexOf(channel.id);
    const swap = index + delta;
    if (index < 0 || swap < 0 || swap >= ids.length) return;
    [ids[index], ids[swap]] = [ids[swap], ids[index]];
    this.run(this.admin.reorderChannels(group.id, ids));
  }

  deleteChannel(channel: CommunityAdminChannel): void {
    if (!confirm(this.t('community.admin.channel.delete_confirm', { name: channel.name }))) return;
    this.deletePendingChannel.set(null);
    this.clearError();
    this.busy.set(true);
    this.admin.deleteChannel(channel.id).subscribe({
      next: () => {
        this.busy.set(false);
        this.load();
      },
      error: err => {
        const code = err?.error?.code;
        if (err?.status === 422 && code === 'community_channel_has_threads' && this.allChannels().some(x => x.channel.id !== channel.id)) {
          // Threads inside: the API refuses until told where they go.
          this.busy.set(false);
          this.deletePendingChannel.set(channel);
          this.deleteMoveTargetId = this.allChannels().find(x => x.channel.id !== channel.id)?.channel.id ?? null;
        } else {
          this.fail(err);
        }
      }
    });
  }

  confirmDeleteWithMove(): void {
    const channel = this.deletePendingChannel();
    const target = this.deleteMoveTargetId;
    if (!channel || target == null) return;
    this.run(this.admin.deleteChannel(channel.id, target), () => this.deletePendingChannel.set(null));
  }

  cancelDeleteWithMove(): void {
    this.deletePendingChannel.set(null);
  }

  // ===========================================================================
  // Rights tab
  // ===========================================================================

  selectRightsGroup(value: string | number | null): void {
    this.rightsGroupId.set(value == null || value === '' ? null : Number(value));
    this.drafts.set({});
    this.extraMatrixRoleIds.set(new Set());
    this.expandedChannels.set(new Set());
    this.clearError();
  }

  private rowKey(target: CommunityPermissionTarget): string {
    return target.channel_id != null ? `c:${target.channel_id}:${target.role_id}` : `g:${target.group_id}:${target.role_id}`;
  }

  defaultRow(group: CommunityAdminGroup, roleId: number): CommunityAdminPermission | undefined {
    return group.default_permissions.find(p => p.role_id === roleId);
  }

  overrideRow(channel: CommunityAdminChannel, roleId: number): CommunityAdminPermission | undefined {
    return channel.permissions.find(p => p.role_id === roleId);
  }

  private emptyFlags(): CommunityPermissionFlags {
    const flags: CommunityPermissionFlags = {};
    this.flags().forEach(f => (flags[f] = false));
    return flags;
  }

  /** Current cell values of a row: the unsaved draft, else the saved row, else all false. */
  rowFlags(target: CommunityPermissionTarget, saved?: CommunityAdminPermission): CommunityPermissionFlags {
    return this.drafts()[this.rowKey(target)] ?? saved?.flags ?? this.emptyFlags();
  }

  isDirty(target: CommunityPermissionTarget): boolean {
    return this.rowKey(target) in this.drafts();
  }

  toggleFlag(target: CommunityPermissionTarget, flag: string, saved?: CommunityAdminPermission): void {
    const key = this.rowKey(target);
    const current = { ...this.rowFlags(target, saved) };
    current[flag] = !current[flag];
    this.drafts.update(d => ({ ...d, [key]: current }));
  }

  discardRow(target: CommunityPermissionTarget): void {
    const key = this.rowKey(target);
    this.drafts.update(d => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  }

  saveRow(target: CommunityPermissionTarget, saved?: CommunityAdminPermission): void {
    const key = this.rowKey(target);
    const flags = this.drafts()[key] ?? saved?.flags;
    if (!flags) return;
    this.run(this.admin.savePermission(target, flags), () => {
      this.discardRow(target);
      if (target.channel_id == null) {
        this.extraMatrixRoleIds.update(set => {
          const next = new Set(set);
          next.delete(target.role_id);
          return next;
        });
      }
    });
  }

  removeRow(target: CommunityPermissionTarget): void {
    const message = target.channel_id != null
      ? this.t('community.admin.rights.remove_override_confirm')
      : this.t('community.admin.rights.remove_default_confirm');
    if (!confirm(message)) return;
    this.run(this.admin.deletePermission(target), () => this.discardRow(target));
  }

  addMatrixRole(): void {
    const id = this.addMatrixRoleId;
    if (id == null) return;
    this.extraMatrixRoleIds.update(set => new Set(set).add(id));
    this.addMatrixRoleId = null;
  }

  /** An override starts as a copy of the group default, so only the differences need clicking. */
  startOverride(group: CommunityAdminGroup, channel: CommunityAdminChannel, roleId: number): void {
    const target = { channel_id: channel.id, role_id: roleId };
    const base = this.defaultRow(group, roleId)?.flags ?? this.emptyFlags();
    this.drafts.update(d => ({ ...d, [this.rowKey(target)]: { ...base } }));
  }

  hasOverrideDraft(channel: CommunityAdminChannel, roleId: number): boolean {
    return this.rowKey({ channel_id: channel.id, role_id: roleId }) in this.drafts();
  }

  toggleChannelExpanded(channelId: number): void {
    this.expandedChannels.update(set => {
      const next = new Set(set);
      next.has(channelId) ? next.delete(channelId) : next.add(channelId);
      return next;
    });
  }

  // ===========================================================================
  // Roles tab
  // ===========================================================================

  private emptyRoleDraft(): RoleDraft {
    return {
      key: '',
      name: '',
      description: '',
      badge_label: '',
      badge_color: 'secondary',
      visibility: 'network',
      capabilities: [],
      rule_type: '',
      rule_value: '',
      position: (this.roles().length ? Math.max(...this.roles().map(r => r.position)) : 0) + 1,
      active: true
    };
  }

  openRoleForm(role?: CommunityAdminRole): void {
    this.clearError();
    this.roleDraft = role
      ? {
          key: role.key,
          name: role.name,
          description: role.description ?? '',
          badge_label: role.badge_label ?? '',
          badge_color: role.badge_color ?? 'secondary',
          visibility: role.visibility,
          capabilities: [...(role.capabilities ?? [])],
          rule_type: role.auto_rule?.type ?? '',
          rule_value: role.auto_rule?.value != null ? String(role.auto_rule.value) : '',
          position: role.position,
          active: role.active
        }
      : this.emptyRoleDraft();
    this.roleFormTarget.set(role ? role.id : 'new');
  }

  cancelRoleForm(): void {
    this.roleFormTarget.set(null);
  }

  editingRole(): CommunityAdminRole | null {
    const target = this.roleFormTarget();
    return typeof target === 'number' ? this.roles().find(r => r.id === target) ?? null : null;
  }

  /** The `member` system role's rule is fixed on the backend (update_role drops auto_rule for it). */
  ruleLocked(): boolean {
    const role = this.editingRole();
    return !!role && role.system && role.key === 'member';
  }

  /** Mirrors the backend's `name.parameterize(separator: '_')` for the preview of a new key. */
  keyPreview(): string {
    return this.roleDraft.name.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
  }

  toggleDraftCapability(cap: string): void {
    const caps = this.roleDraft.capabilities;
    this.roleDraft.capabilities = caps.includes(cap) ? caps.filter(c => c !== cap) : [...caps, cap];
  }

  ruleNeedsNumber(type: string): boolean {
    return COMMUNITY_RULE_TYPES_WITH_NUMBER.includes(type);
  }

  ruleNeedsRank(type: string): boolean {
    return type === COMMUNITY_RULE_TYPE_RANK;
  }

  onRuleTypeChange(type: string): void {
    this.roleDraft.rule_type = type;
    this.roleDraft.rule_value = this.ruleNeedsRank(type) ? (this.rankCodes()[0] ?? '') : '';
  }

  private draftRule(): CommunityAutoRule | null {
    const type = this.roleDraft.rule_type;
    if (!type) return null;
    if (this.ruleNeedsNumber(type)) return { type, value: Number(this.roleDraft.rule_value) };
    if (this.ruleNeedsRank(type)) return { type, value: this.roleDraft.rule_value };
    return { type };
  }

  roleDraftValid(): boolean {
    if (!this.roleDraft.name.trim()) return false;
    const type = this.roleDraft.rule_type;
    if (this.ruleNeedsNumber(type)) return /^\d+$/.test(this.roleDraft.rule_value) && Number(this.roleDraft.rule_value) > 0;
    if (this.ruleNeedsRank(type)) return !!this.roleDraft.rule_value;
    return true;
  }

  submitRoleForm(): void {
    const target = this.roleFormTarget();
    if (target == null || !this.roleDraftValid()) return;
    const payload: CommunityRolePayload = {
      name: this.roleDraft.name.trim(),
      description: this.roleDraft.description.trim() || null,
      badge_label: this.roleDraft.badge_label.trim() || null,
      badge_color: this.roleDraft.badge_color.trim() || null,
      visibility: this.roleDraft.visibility,
      capabilities: this.roleDraft.capabilities,
      auto_rule: this.draftRule(),
      position: Number(this.roleDraft.position) || 0,
      active: this.roleDraft.active
    };
    if (target === 'new' && this.roleDraft.key.trim()) payload.key = this.roleDraft.key.trim();
    this.run(
      target === 'new' ? this.admin.createRole(payload) : this.admin.updateRole(target, payload),
      () => this.roleFormTarget.set(null)
    );
  }

  deleteRole(role: CommunityAdminRole): void {
    const reason = prompt(this.t('community.admin.role.delete_reason_prompt', { name: role.name }));
    if (reason == null) return;
    if (!reason.trim()) {
      this.error.set('community.errors.reason_required');
      return;
    }
    this.run(this.admin.deleteRole(role.id, reason.trim()), () => {
      if (this.grantsRoleId() === role.id) this.grantsRoleId.set(null);
    });
  }

  ruleLabel(role: CommunityAdminRole): string {
    const rule = role.auto_rule;
    if (!rule?.type) return this.t('community.admin.rule.none');
    const name = this.t(`community.admin.rule.${rule.type}`);
    return rule.value != null && rule.value !== '' ? `${name}: ${rule.value}` : name;
  }

  badgeClass(color: string | null | undefined): string {
    const c = (color || 'secondary').toLowerCase();
    if (!BADGE_COLORS.includes(c)) return 'badge text-white';
    return `badge bg-${c}${c === 'warning' || c === 'info' || c === 'light' ? ' text-dark' : ''}`;
  }

  badgeStyle(color: string | null | undefined): string | null {
    const c = (color || '').toLowerCase();
    return c && !BADGE_COLORS.includes(c) ? color! : null;
  }

  openGrants(role: CommunityAdminRole): void {
    this.grantsRoleId.set(role.id);
    this.selectTab('grants');
  }

  // ===========================================================================
  // Grants tab
  // ===========================================================================

  private emptyGrantDraft(): GrantDraft {
    return { user: null, user_id: '', scope: this.canManageRoles() ? 'global' : 'group', scope_id: null, reason: '', expires_at: '' };
  }

  selectGrantsRole(value: string | number | null): void {
    this.grantsRoleId.set(value == null || value === '' ? null : Number(value));
    this.grantFormOpen.set(false);
    this.loadAssignments(1);
  }

  onAssignmentSearch(term: string): void {
    this.assignmentSearchTerm = term;
    this.assignmentSearch$.next(term.trim());
  }

  clearAssignmentSearch(): void {
    if (!this.assignmentSearchTerm && !this.assignmentSearch()) return;
    this.assignmentSearchTerm = '';
    this.assignmentSearch$.next('');
  }

  toggleIncludeRevoked(): void {
    this.includeRevoked.update(v => !v);
    this.loadAssignments(1);
  }

  loadAssignments(page: number): void {
    const roleId = this.grantsRoleId();
    if (roleId == null) {
      this.assignments.set([]);
      return;
    }
    this.assignmentsLoading.set(true);
    this.admin.listAssignments(roleId, { page, perPage: 25, includeRevoked: this.includeRevoked(), q: this.assignmentSearch() }).subscribe({
      next: res => {
        this.assignments.set(res.data);
        this.assignmentsPage.set(res.page);
        this.assignmentsTotalPages.set(res.total_pages);
        this.assignmentsTotal.set(res.total_count);
        this.assignmentsLoading.set(false);
      },
      error: err => {
        this.assignmentsLoading.set(false);
        this.assignments.set([]);
        this.error.set(communityErrorKey(err));
      }
    });
  }

  openGrantForm(): void {
    this.clearError();
    this.grantDraft = this.emptyGrantDraft();
    this.userResults.set([]);
    this.userSearchTerm = '';
    this.grantFormOpen.set(true);
  }

  cancelGrantForm(): void {
    this.grantFormOpen.set(false);
  }

  onUserSearch(term: string): void {
    this.userSearchTerm = term;
    this.grantDraft.user = null;
    this.userSearch$.next(term);
  }

  pickUser(user: CommunityAdminUser): void {
    this.grantDraft.user = user;
    this.grantDraft.user_id = String(user.id);
    this.userSearchTerm = this.userLabel(user);
    this.userResults.set([]);
  }

  userLabel(user: CommunityAdminUser | null | undefined): string {
    if (!user) return '—';
    const name = user.full_name || user.username || '';
    return name ? `${name} (${user.email})` : user.email;
  }

  onGrantScopeChange(scope: 'global' | 'group' | 'channel'): void {
    this.grantDraft.scope = scope;
    this.grantDraft.scope_id = scope === 'group'
      ? this.groups()[0]?.id ?? null
      : scope === 'channel' ? this.allChannels()[0]?.channel.id ?? null : null;
  }

  grantDraftValid(): boolean {
    const d = this.grantDraft;
    const userId = d.user?.id ?? Number(d.user_id);
    if (!userId || Number.isNaN(userId) || userId <= 0) return false;
    if (!d.reason.trim()) return false;
    if (d.scope !== 'global' && d.scope_id == null) return false;
    return true;
  }

  submitGrantForm(): void {
    const roleId = this.grantsRoleId();
    if (roleId == null || !this.grantDraftValid()) return;
    const d = this.grantDraft;
    const scopeType: CommunityAssignmentScopeType | null =
      d.scope === 'group' ? 'CommunityGroup' : d.scope === 'channel' ? 'CommunityChannel' : null;
    this.clearError();
    this.busy.set(true);
    this.admin.createAssignment({
      user_id: d.user?.id ?? Number(d.user_id),
      role_id: roleId,
      scope_type: scopeType,
      scope_id: scopeType ? d.scope_id : null,
      reason: d.reason.trim(),
      expires_at: d.expires_at || null
    }).subscribe({
      next: () => {
        this.busy.set(false);
        this.grantFormOpen.set(false);
        this.loadAssignments(1);
        this.load(); // assignment_count
      },
      error: err => this.fail(err)
    });
  }

  revoke(assignment: CommunityAssignment): void {
    const reason = prompt(this.t('community.admin.grants.revoke_prompt', { user: this.userLabel(assignment.user) }));
    if (reason == null) return;
    if (!reason.trim()) {
      this.error.set('community.errors.reason_required');
      return;
    }
    this.clearError();
    this.busy.set(true);
    this.admin.revokeAssignment(assignment.id, reason.trim()).subscribe({
      next: () => {
        this.busy.set(false);
        this.loadAssignments(this.assignmentsPage());
        this.load();
      },
      error: err => this.fail(err)
    });
  }

  assignmentStatus(a: CommunityAssignment): 'active' | 'revoked' | 'expired' {
    if (a.revoked_at) return 'revoked';
    if (a.expires_at && new Date(a.expires_at).getTime() < Date.now()) return 'expired';
    return a.active ? 'active' : 'expired';
  }

  scopeLabel(a: CommunityAssignment): string {
    if (!a.scope_type) return this.t('community.admin.grants.scope_global');
    const kind = a.scope_type === 'CommunityGroup'
      ? this.t('community.admin.grants.scope_group')
      : this.t('community.admin.grants.scope_channel');
    return `${kind}: ${a.scope_name ?? '#' + a.scope_id}`;
  }

  // ===========================================================================
  // Communication tab: who may message whom (§8/§9)
  // ===========================================================================

  loadCommunication(): void {
    this.communicationLoading.set(true);
    this.admin.getCommunication().subscribe({
      next: res => {
        this.communication.set(res);
        this.communicationLoading.set(false);
      },
      error: err => {
        this.communicationLoading.set(false);
        this.fail(err);
      }
    });
  }

  private emptyRuleDraft(): RuleDraft {
    return { from_role_id: null, to_role_id: null, mode: 'any', depth: 1, description: '', active: true };
  }

  openRuleForm(rule?: CommunityCommunicationRule): void {
    this.clearError();
    this.ruleDraft = rule
      ? {
          from_role_id: rule.from_role_id,
          to_role_id: rule.to_role_id,
          mode: rule.mode,
          depth: rule.depth,
          description: rule.description ?? '',
          active: rule.active
        }
      : this.emptyRuleDraft();
    this.ruleFormTarget.set(rule ? rule.id : 'new');
  }

  cancelRuleForm(): void {
    this.ruleFormTarget.set(null);
  }

  submitRuleForm(): void {
    const target = this.ruleFormTarget();
    if (target == null) return;
    const d = this.ruleDraft;
    const payload = {
      from_role_id: d.from_role_id,
      to_role_id: d.to_role_id,
      mode: d.mode,
      depth: d.mode === 'sponsor_line' ? Number(d.depth) || 1 : 1,
      description: d.description.trim() || null,
      active: d.active
    };
    this.run(
      target === 'new' ? this.admin.createRule(payload) : this.admin.updateRule(target, payload),
      () => {
        this.ruleFormTarget.set(null);
        this.loadCommunication();
      }
    );
  }

  toggleRuleActive(rule: CommunityCommunicationRule): void {
    this.run(this.admin.updateRule(rule.id, { active: !rule.active }), () => this.loadCommunication());
  }

  deleteRule(rule: CommunityCommunicationRule): void {
    if (!confirm(this.t('community.admin.communication.rule_delete_confirm'))) return;
    this.run(this.admin.deleteRule(rule.id), () => this.loadCommunication());
  }

  moveRule(rule: CommunityCommunicationRule, delta: -1 | 1): void {
    const ids = this.rules().map(r => r.id);
    const index = ids.indexOf(rule.id);
    const swap = index + delta;
    if (index < 0 || swap < 0 || swap >= ids.length) return;
    const other = this.rules()[swap];
    this.busy.set(true);
    this.clearError();
    this.admin.updateRule(rule.id, { position: other.position }).subscribe({
      next: () => this.admin.updateRule(other.id, { position: rule.position }).subscribe({
        next: () => {
          this.busy.set(false);
          this.loadCommunication();
        },
        error: err => this.fail(err)
      }),
      error: err => this.fail(err)
    });
  }

  ruleModeLabel(mode: CommunityCommunicationMode): string {
    return this.t('community.admin.communication.mode.' + mode);
  }

  ruleRoleLabel(roleKey: string | null): string {
    return roleKey ? this.roleByKey(roleKey)?.name ?? roleKey : this.t('community.admin.communication.any_role');
  }

  // --- Contact grants ------------------------------------------------------------

  private emptyContactGrantDraft(): ContactGrantDraft {
    return { user: null, user_id: '', contact: null, contact_id: '', reason: '', expires_at: '', both_ways: false };
  }

  openContactGrantForm(): void {
    this.clearError();
    this.contactGrantDraft = this.emptyContactGrantDraft();
    this.contactUserResults.set([]);
    this.contactUserSearchTerm = '';
    this.contactContactResults.set([]);
    this.contactContactSearchTerm = '';
    this.contactGrantFormOpen.set(true);
  }

  cancelContactGrantForm(): void {
    this.contactGrantFormOpen.set(false);
  }

  onContactUserSearch(term: string): void {
    this.contactUserSearchTerm = term;
    this.contactGrantDraft.user = null;
    this.contactUserSearch$.next(term);
  }

  onContactContactSearch(term: string): void {
    this.contactContactSearchTerm = term;
    this.contactGrantDraft.contact = null;
    this.contactContactSearch$.next(term);
  }

  pickContactUser(u: CommunityAdminUser): void {
    this.contactGrantDraft.user = u;
    this.contactGrantDraft.user_id = String(u.id);
    this.contactUserSearchTerm = this.userLabel(u);
    this.contactUserResults.set([]);
  }

  pickContact(u: CommunityAdminUser): void {
    this.contactGrantDraft.contact = u;
    this.contactGrantDraft.contact_id = String(u.id);
    this.contactContactSearchTerm = this.userLabel(u);
    this.contactContactResults.set([]);
  }

  contactGrantDraftValid(): boolean {
    const d = this.contactGrantDraft;
    const userId = d.user?.id ?? Number(d.user_id);
    const contactId = d.contact?.id ?? Number(d.contact_id);
    if (!userId || Number.isNaN(userId) || userId <= 0) return false;
    if (!contactId || Number.isNaN(contactId) || contactId <= 0) return false;
    if (userId === contactId) return false;
    if (!d.reason.trim()) return false;
    return true;
  }

  submitContactGrantForm(): void {
    if (!this.contactGrantDraftValid()) return;
    const d = this.contactGrantDraft;
    this.clearError();
    this.busy.set(true);
    this.admin.createContactGrant({
      user_id: d.user?.id ?? Number(d.user_id),
      contact_id: d.contact?.id ?? Number(d.contact_id),
      reason: d.reason.trim(),
      expires_at: d.expires_at || null,
      both_ways: d.both_ways
    }).subscribe({
      next: () => {
        this.busy.set(false);
        this.contactGrantFormOpen.set(false);
        this.loadCommunication();
      },
      error: err => this.fail(err)
    });
  }

  revokeContactGrant(grant: CommunityContactGrant): void {
    if (!confirm(this.t('community.admin.communication.grant_revoke_confirm'))) return;
    this.run(this.admin.revokeContactGrant(grant.id), () => this.loadCommunication());
  }

  // ===========================================================================
  // Statistics tab
  // ===========================================================================

  loadStats(days = this.statsDays()): void {
    this.statsDays.set(days);
    this.statsLoading.set(true);
    this.admin.getStats(days).subscribe({
      next: res => {
        this.stats.set(res);
        this.statsLoading.set(false);
      },
      error: err => {
        this.statsLoading.set(false);
        this.fail(err);
      }
    });
  }

  selectStatsDays(days: string | number): void {
    this.loadStats(Number(days) || 30);
  }

  /** Bar width relative to the busiest week, for the dependency-free weekly chart. */
  weekBarWidth(count: number, field: 'threads' | 'posts' | 'reactions'): number {
    const weeks = this.stats()?.weekly ?? [];
    const max = Math.max(1, ...weeks.map(w => w[field]));
    return Math.round((count / max) * 100);
  }

  // ===========================================================================
  // Webhooks tab (`manage_roles` only)
  // ===========================================================================

  private emptyWebhookDraft(): WebhookDraft {
    return { name: '', url: '', events: [], active: true, rotate_secret: false };
  }

  loadWebhooks(): void {
    this.webhooksLoading.set(true);
    this.admin.getWebhooks().subscribe({
      next: res => {
        this.webhooks.set(res.endpoints);
        this.webhookEvents.set(res.events);
        this.webhookDeliveries.set(res.deliveries);
        this.webhooksLoading.set(false);
        this.webhooksLoaded.set(true);
      },
      error: err => {
        this.webhooksLoading.set(false);
        this.fail(err);
      }
    });
  }

  openWebhookForm(webhook?: CommunityWebhookEndpoint): void {
    this.clearError();
    this.webhookSecret.set(null);
    this.webhookDraft = webhook
      ? { name: webhook.name, url: webhook.url, events: [...webhook.events], active: webhook.active, rotate_secret: false }
      : this.emptyWebhookDraft();
    this.webhookFormTarget.set(webhook ? webhook.id : 'new');
  }

  cancelWebhookForm(): void {
    this.webhookFormTarget.set(null);
  }

  toggleWebhookEvent(event: string): void {
    const events = this.webhookDraft.events;
    this.webhookDraft.events = events.includes(event) ? events.filter(e => e !== event) : [...events, event];
  }

  webhookDraftValid(): boolean {
    return !!this.webhookDraft.name.trim() && /^https:\/\/\S+$/i.test(this.webhookDraft.url.trim()) && this.webhookDraft.events.length > 0;
  }

  submitWebhookForm(): void {
    const target = this.webhookFormTarget();
    if (target == null || !this.webhookDraftValid()) return;
    const payload: CommunityWebhookPayload = {
      name: this.webhookDraft.name.trim(),
      url: this.webhookDraft.url.trim(),
      events: this.webhookDraft.events,
      active: this.webhookDraft.active
    };
    if (target !== 'new' && this.webhookDraft.rotate_secret) payload.rotate_secret = true;
    this.clearError();
    this.busy.set(true);
    const request = target === 'new' ? this.admin.createWebhook(payload) : this.admin.updateWebhook(target, payload);
    request.subscribe({
      next: res => {
        this.busy.set(false);
        this.webhookFormTarget.set(null);
        if (res.secret) this.webhookSecret.set(res.secret);
        this.loadWebhooks();
      },
      error: err => this.fail(err)
    });
  }

  toggleWebhookActive(webhook: CommunityWebhookEndpoint): void {
    this.clearError();
    this.busy.set(true);
    this.admin.updateWebhook(webhook.id, { name: webhook.name, url: webhook.url, events: webhook.events, active: !webhook.active }).subscribe({
      next: () => {
        this.busy.set(false);
        this.loadWebhooks();
      },
      error: err => this.fail(err)
    });
  }

  deleteWebhook(webhook: CommunityWebhookEndpoint): void {
    if (!confirm(this.t('community.admin.webhooks.delete_confirm', { name: webhook.name }))) return;
    this.clearError();
    this.busy.set(true);
    this.admin.deleteWebhook(webhook.id).subscribe({
      next: () => {
        this.busy.set(false);
        this.loadWebhooks();
      },
      error: err => this.fail(err)
    });
  }

  testWebhook(webhook: CommunityWebhookEndpoint): void {
    this.clearError();
    this.busy.set(true);
    this.admin.testWebhook(webhook.id).subscribe({
      next: () => {
        this.busy.set(false);
        this.loadWebhooks();
      },
      error: err => this.fail(err)
    });
  }

  deliveriesFor(endpointId: number): CommunityWebhookDelivery[] {
    return this.webhookDeliveries().filter(d => d.endpoint_id === endpointId);
  }
}
